/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The field catalog — one declaration of every addressable name** (`PF`, D10/D20).
 *
 * Before this there were **five** hand-kept lists and three of them were ways to *offer*
 * a macro: the resolver's `switch`, the console's `RESOLVABLE_MACRO_NAMES`,
 * `MACRO_DEFAULTS`, `MACRO_HELPER_SUGGESTIONS` and `VAST_PARAM_CATALOG[].defaultMacro`.
 * Every vocabulary defect measured on 2026-08-17/18 is one of them disagreeing with
 * another — and the answer is not a longer list or more care, it is **one declaration the
 * others are checked against** (§6a).
 *
 * ## Why this file has no imports, and must keep none
 *
 * `apps/admin-console` is a separate Vite app that must not pull in Node-only DTO code, so
 * it has always **mirrored** DTOs by hand. A mirrored catalog would reintroduce exactly the
 * drift being removed. Instead the console **aliases this one file** (`@field-catalog` in
 * `vite.config.ts` and `tsconfig.app.json`), which costs no build step: measured
 * 2026-08-18, `tsc -b` type-checks it transitively and Vite inlines its values into the
 * bundle, so nothing is resolved at runtime and no `libs` directory ships.
 *
 * That is only safe while the file imports **nothing** — one import and the console starts
 * pulling the DTO graph across a boundary that exists for a reason.
 * `field-catalog.spec.ts` fails if any appears.
 *
 * ## What an entry claims — D20's three legs
 *
 * Being here is a claim that the name **is offered** by the editor, that a mapping to it
 * **resolves**, and that something **writes** the path it reads. Those are checkable
 * against this file, which is what makes it a declaration rather than a list.
 *
 * ## What is deliberately not here yet
 *
 * - **A default `sendAs`** (D23 · R4). Its type lives in `wire-rendering.ts`, and importing
 *   it would break the zero-import rule above, while restating the union here would be the
 *   duplication this file exists to end. It lands when the console consumes the catalog
 *   and the boundary question is answered for more than one module.
 * - **`suppliedBy` and `importance`** (D27). Those are properties of a *destination*
 *   catalog — where a value is written to — and this is the catalog of *sources* a value
 *   can be read from. Two different questions; see `ortb-field-catalog.ts`.
 */

/**
 * **What makes a value wrong, or merely suspicious** (`D41`, `#1192` · `#914`).
 *
 * `D36` ratified that a validator is part of a **field's definition**, and that the authored
 * definition and ours are one shape — *"anything the user can add, we'd have in our own"*. Measured
 * the day it was ratified, the asymmetry ran the wrong way: §7 specifies *acceptable values /
 * validation rules* for an authored field while both catalogs could express **neither**, so an
 * operator could declare something the system cannot. This is the shape that closes it.
 *
 * ## It lives here because this file imports nothing
 *
 * A constraint is a property of a **value**, and both catalogs need it — `destination-field.ts`
 * already imports this module and this one may import nothing at all, so the boundary decides the
 * placement rather than taste. `D34` keeps sources and destinations apart as *catalogs*; it does
 * not ask them to describe a value in two different languages.
 *
 * ## Which catalog carries which constraint
 *
 * | Declared on | Says | Example |
 * | --- | --- | --- |
 * | {@link FieldCatalogEntry.constraint} | the **concept's** grammar, true wherever it is sent | `us_privacy` is a CCPA string on a VAST query parameter and in an ORTB body alike |
 * | `DestinationField.constraint` | this **slot's** own rule | `imp.video.plcmt` accepts 1–4, which is a fact about that receiver |
 *
 * **A concept's constraint reaches a destination without being restated**, because a destination
 * that carries one already says so: `value: { from: 'derived', source: 'us_privacy' }`. That link is
 * declared and already gated — `ad-server-presets.fields.spec.ts` asserts every `derived` source
 * resolves to a real catalog concept — so the checker follows it rather than the two catalogs
 * keeping a copy each.
 *
 * A third case needs no declaration at all: where the value is a member of a vocabulary **we** own
 * (`value: { from: 'concept' }`), the acceptable set is `conceptMembersFor`, version-scoped and
 * built since `#1112`. Declaring an enum beside it would be two answers to one question, and the
 * version-scoped one would be the one that goes stale.
 *
 * ## Two kinds, and the severity is derived rather than declared
 *
 * A **constraint** (`values` · `pattern` · `range`) says the value is *wrong*; a **sentinel** says
 * it is valid and probably not what was meant. That is CLAUDE.md's own three-way split arriving in
 * a type — *error: wrong, and you may proceed · warning: this may not be what you meant* — and it is
 * why `#914`'s `device.ifa = 000…0` is not a syntax rule: it is a structurally perfect UUID that
 * means *no advertising identifier* to every exchange, and a purely syntactic checker passes it.
 *
 * **Neither declares its own level.** A rule that carried one would let a vendor template mark
 * everything blocking, and the level is decided by which *kind* matched — so an author chooses what
 * they know (is this wrong, or merely odd) rather than a severity they have no basis for.
 */
export interface ValueConstraint {
  /**
   * The complete set of acceptable values — a closed vocabulary the **receiver** owns.
   *
   * Not for a vocabulary *we* own: that is `DestinationValue`'s `concept` variant, whose members
   * are version-scoped and already derivable. Compared case-insensitively, because a receiver that
   * publishes `Y`/`N` accepts them from an operator who typed lowercase and the alternative is an
   * error message about capitalisation on a value that works.
   */
  readonly values?: readonly string[];

  /**
   * A grammar the value must match.
   *
   * **`describes` is not optional, and that is the whole reason this is an object.** A message
   * quoting the expression describes our mechanism rather than the operator's outcome (`#1045`):
   * *"`us_privacy` expects a CCPA string — a version digit then three of Y, N or -"* is actionable
   * and `/^[0-9][YN-]{3}$/` is not, to the person who typed `yes`.
   *
   * **Anchor the expression, and keep it linear.** The catalog is a code module today and becomes
   * an **authored artifact** under `D37` — at which point an operator's regex is executed against
   * request values, so a catastrophically backtracking pattern is a denial of service written from
   * the authoring surface. The checker bounds the input it grades for that reason; it is a bound,
   * not a solution, and the solution belongs with the store.
   */
  readonly pattern?: { readonly source: string; readonly describes: string };

  /**
   * Inclusive numeric bounds.
   *
   * **Its own pair rather than a degenerate `values` list**, because the two are different claims:
   * `plcmt ∈ {1,2,3,4}` is four *meanings* and `bidfloor ≥ 0` is a bound with no members to list.
   * Spelling the second as an enum is unrepresentable at any useful width, and spelling the first
   * as a range loses the fact that the members are named things.
   */
  readonly range?: { readonly min?: number; readonly max?: number };

  /**
   * Values that pass every rule above and still probably indicate a mistake, with what each means.
   *
   * `#914`'s requirement, and the reason it is not a `pattern`: *valid* and *intended* are different
   * questions, and answering both with one rule would put two answers on one row (`N11f`). The
   * `means` text is the message — the operator is told what the value says to an exchange, not that
   * it is on a list.
   */
  readonly sentinels?: readonly { readonly value: string; readonly means: string }[];
}

/* ------------------------------------------------------------------------- *
 * The grammars, named once (`4a`, `#914`)
 * ------------------------------------------------------------------------- */

/**
 * **One grammar, one statement — whether it is a concept's or a slot's** (`4a`).
 *
 * A concept declares its grammar and every destination that says it carries the concept reaches it
 * through the link (`D41`). What the link cannot reach is a slot carrying the *same grammar for a
 * different concept*: `device.language` is the **browser's** language and `content.language` is the
 * content's, so one of them cannot be the other's `derived` source — and both are ISO-639-1.
 * Restating the expression at the second slot is the `X3` shape at the width of a regex, where the
 * two copies would drift in a way nothing compares.
 *
 * So the grammar is named here and both sites spread it. This file is the right home for the same
 * reason {@link ValueConstraint} is: it imports nothing, and both catalogs already import it.
 *
 * **Every expression is anchored and linear.** A constraint becomes operator-authored under `D37`,
 * at which point a catastrophically backtracking pattern is a denial of service written from the
 * authoring surface — these are what the shipped set must not be an example of.
 */

/** An IPv4 dotted quad, or an IPv6 address. */
export const IP_ADDRESS: ValueConstraint = {
  pattern: {
    // Two arms rather than one loose expression: the IPv4 arm is exact (a dotted quad with each
    // octet bounded), and the IPv6 arm is deliberately **permissive** — hex groups and colons, with
    // the IPv4-mapped tail allowed. An exact IPv6 grammar is where a false positive would come
    // from, and a false positive is worse than an omission (`#1002`).
    source:
      '^((25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)(\\.(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)){3}|[0-9A-Fa-f:]*:[0-9A-Fa-f:.]*)$',
    describes: 'an IP address — IPv4 such as 203.0.113.4, or IPv6',
  },
  sentinels: [
    {
      value: '0.0.0.0',
      // Structurally a valid address and semantically *we do not know where this viewer is*, which
      // is the `ifa` zero case one field along: an exchange reads it as no address at all and
      // cannot geo-target, so the request under-monetises silently.
      means: 'tells an exchange there is no viewer address, so it cannot geo-target the request.',
    },
  ],
};

/**
 * A UUID, the form IAB's OTT/CTV IFA guidance requires — with the zeroed one called out.
 *
 * The sentinel is `#914`'s own example and a defect this project has already shipped (`#895`): a
 * structurally perfect UUID that every exchange reads as *no advertising identifier*. A purely
 * syntactic checker passes it, which is exactly why `sentinels` is a separate kind.
 */
export const ADVERTISING_ID: ValueConstraint = {
  pattern: {
    source: '^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$',
    describes: 'a UUID such as 38400000-8cf0-11bd-b23e-10b96e40000d',
  },
  sentinels: [
    {
      value: '00000000-0000-0000-0000-000000000000',
      means: 'is the zeroed IFA — every exchange reads it as no advertising identifier at all.',
    },
  ],
};

/**
 * A TCF transparency-and-consent string.
 *
 * **The claim is base64url and a length, not the segment layout.** A TCF v2 core segment begins `C`
 * and a v1.1 one begins `B`, and both are permitted here; what this rejects is the value `#914` is
 * about — `yes`, `1`, `true` — typed where a consent string goes. Reading the segments would be a
 * decoder, which is a different thing from a grammar and belongs nowhere near an authoring surface.
 */
export const CONSENT_STRING: ValueConstraint = {
  pattern: {
    source: '^[A-Za-z0-9_-]{16,}(\\.[A-Za-z0-9_-]+)*$',
    describes: 'a TC string — base64url text of at least 16 characters, beginning "C" for TCF v2',
  },
  // **No sentinel for `1`, and the absence is a decision.** The flag that says GDPR applies is
  // what an operator most often types here, and a sentinel naming that redirect could never fire:
  // `1` fails the pattern, errors run before warnings, and `checkRule` returns on the first
  // failure. The alternative — relaxing the pattern so the sentinel is reachable — would let `yes`
  // through silently, which is the value `#914` was raised from. The error sentence says what a TC
  // string is, which is what the operator needs either way.
};

/** `0` or `1` — the wire form of a flag, on either protocol. */
export const FLAG_0_1: ValueConstraint = { values: ['0', '1'] };

/** An absolute URL. The scheme is the whole check: a bare domain here is the common mistake. */
export const ABSOLUTE_URL: ValueConstraint = {
  pattern: { source: '^https?://[^\\s]+$', describes: 'an absolute URL beginning http:// or https://' },
};

/** A hostname — no scheme, no path, which is what distinguishes it from {@link ABSOLUTE_URL}. */
export const HOSTNAME: ValueConstraint = {
  pattern: {
    source: '^[A-Za-z0-9][A-Za-z0-9.-]*\\.[A-Za-z]{2,}$',
    describes: 'a hostname such as example.com, with no scheme and no path',
  },
};

/**
 * A bundle or store id.
 *
 * **Weak on purpose.** A bundle is `com.example.app` on Android, a numeric store id on iOS and a
 * domain on much of CTV, so there is no shape all three share — what they share is that **none of
 * them contains a space**, which is the mistake `#914` names: the app's *display name* written
 * where its bundle goes.
 */
export const BUNDLE_ID: ValueConstraint = {
  pattern: {
    source: '^[^\\s]+$',
    describes: "a bundle or store id such as com.example.app — not the app's display name",
  },
};

/** ISO-4217, the three-letter currency code. */
export const CURRENCY_CODE: ValueConstraint = {
  pattern: { source: '^[A-Za-z]{3}$', describes: 'a three-letter ISO-4217 currency code such as USD' },
};

/**
 * ISO-639-1, the two-letter language code.
 *
 * A region suffix (`en-US`) is accepted silently: the field is specified as ISO-639-1 and a great
 * deal of real traffic carries BCP-47, so refusing it would report somebody's working integration
 * as broken. What this rejects is `English`.
 */
export const LANGUAGE_CODE: ValueConstraint = {
  pattern: {
    source: '^[A-Za-z]{2}(-[A-Za-z0-9]{2,8})?$',
    describes: 'a two-letter ISO-639-1 language code such as en',
  },
};

/** A MIME type. Catches `mp4` written where `video/mp4` goes, which is the whole point of it. */
export const MIME_TYPE: ValueConstraint = {
  pattern: {
    source: '^[A-Za-z][A-Za-z0-9.+-]*/[A-Za-z0-9.+*-]+$',
    describes: 'a MIME type such as video/mp4',
  },
};

/**
 * ISO-3166-1 alpha-3, **checked loosely on purpose.**
 *
 * OpenRTB specifies alpha-3 and a great deal of real traffic carries alpha-2, so two letters are
 * accepted silently: refusing them would report a working integration as broken. What this rejects
 * is `United States`.
 */
export const COUNTRY_CODE: ValueConstraint = {
  pattern: {
    source: '^[A-Za-z]{2,3}$',
    describes: 'a three-letter ISO-3166-1 country code such as USA',
  },
};

/** Seconds, or any other count that cannot be negative. */
export const NON_NEGATIVE: ValueConstraint = { range: { min: 0 } };

/** A pixel dimension, which cannot be zero either. */
export const POSITIVE: ValueConstraint = { range: { min: 1 } };

/** One addressable value, with every spelling that reaches it. */
export interface FieldCatalogEntry {
  /**
   * The canonical spelling — the first `case` label of the resolver's group, and the one
   * the editor should offer.
   */
  readonly name: string;

  /**
   * Other spellings that resolve to the same value.
   *
   * **Aliases are real, not tolerated.** The resolver has always been liberal here by
   * design (`ifa` / `idfa` / `did` / `device_id` are one concept), because real tags carry
   * all of them. What is *not* permitted is an alias whose **values** differ from its
   * canonical — see the note on `rating` below, and D25.
   */
  readonly aliases?: readonly string[];

  /**
   * The context path an operator may write instead of the macro, where one exists.
   *
   * **A macro is a shortcut, not an access path** (D24), so this is the form that always
   * works and the macro is the convenience. Absent means the value has no single path —
   * either because the resolver reads a **fallback chain** across several locations, or
   * because bidBrain **produces** it (see {@link produced}).
   */
  readonly path?: string;

  /**
   * True when the resolver picks across several locations rather than reading one path.
   *
   * **This is #1077's finding made declarable**: the macro is the fallback chain and the
   * path is one link, so the two forms can resolve differently and every surface that
   * treats them as interchangeable is making an unchecked claim. Where this is set, the
   * `path` is the *primary* location, not an equivalent.
   */
  readonly chain?: true;

  /**
   * bidBrain produces this value rather than reading it from the session, so there is no
   * path to it and none should be offered (D17/D22 — *do we produce it?*).
   */
  readonly produced?: true;

  /**
   * What shape the value takes on the wire.
   *
   * **A flag is not a string**, and the distinction is load-bearing: `resolveContextValue`
   * normalises `dnt`, `lmt`, `gdpr` and `coppa` through `flag()` to the 0/1 both protocols
   * require, and **refuses a value it cannot read as a flag** rather than passing text
   * through. An editor that validated them as free text would accept `"yes"` and send
   * nothing. Absent means a string.
   */
  readonly kind?: 'flag' | 'number';

  /**
   * The name the editor **offers**, when the concept is one we suggest at all.
   *
   * **Deliberately separate from {@link name}**, which is the resolver's first `case` label
   * and therefore an implementation fact. The suggested name is an *editorial* choice with
   * reasons — `model` over `device_model` because tags carry the short form, `genres` over
   * `genre` because it is an array and the singular misleads, `content_rating` over `rating`
   * because a rating could be a score. Projecting the picker from `name` would silently
   * reverse five such decisions.
   *
   * **Absent means not suggested** — every alias still resolves, still matches a pasted
   * parameter and still renders as a known macro; it simply is not offered. Offering all
   * spellings put `{ip}` beside `{ipAddress}` and four names for one device id, which is a
   * choice the operator has no information to make and where two people pick differently
   * and their sources stop looking alike.
   */
  readonly suggest?: string;

  /** What the value is, in the operator's terms. */
  readonly description: string;

  /** Grouping for the picker, mirroring the resolver's own section comments. */
  readonly section: string;

  /**
   * **This value belongs to the request, not to the configuration** (#1188).
   *
   * A literal sampled from somebody's real tag or captured request — an IP, a user agent, an
   * advertising id, a session id, a cache buster — must never be frozen into a stored mapping:
   * it would then be sent for every viewer, which is a stale-identity hazard at best and a
   * stranger's personal data at worst. So a paste maps the name and **discards the literal**,
   * saying so.
   *
   * Absent means the literal may be retained as the mapping's *default*, which is a different and useful
   * thing: on a dynamic field the default is consulted only after resolution fails, so keeping
   * a pasted `app_bundle` means *"send this if the session didn't carry one"* and keeps a source
   * bidding when a session is thin.
   *
   * **Promoted here from `CONTEXT_PARAMETERS`** (`libs/adapters/ad-tag.adapter.ts`), where it was
   * declared per **VAST parameter spelling** and reachable by the VAST paste alone. It is a
   * property of the *concept*: `device.ip` in an ORTB body is per-request data for exactly the
   * reason `ip` in a query string is, and the ORTB body paste had no way to ask (#1187).
   */
  readonly perRequest?: true;

  /**
   * **A regulatory assertion, which we forward and never invent** (#912, #1188).
   *
   * Stronger than {@link perRequest} and stated separately because the *reason* differs, and the
   * operator is told the reason: a stored consent string asserts a privacy position for viewers
   * who never took it. Every consent entry is also `perRequest`, and a spec asserts that — but a
   * retained IP is a stale value while a retained consent string is a claim made on somebody's
   * behalf, and one message cannot say both.
   *
   * It is also what makes absence *actionable*: because we never fill one in, a request may carry
   * no consent signal at all, which an exchange may refuse. That is the one class where the
   * editor should still say something (`ORTB_MERGE_BASE`'s `forwarded-if-sent`).
   */
  readonly consent?: true;

  /**
   * **What makes a value of this concept wrong** (`D41`, `#1192`) — see {@link ValueConstraint}.
   *
   * Declared on the *concept* because it is true wherever the value is sent: `us_privacy` is a CCPA
   * string as a VAST query parameter and as `regs.ext.us_privacy` alike, which is the parity `#914`
   * asks for and the reason it cannot live on one destination.
   *
   * **Absent says nothing**, exactly as `DestinationField.value` does — never *any value is fine*,
   * which is a positive claim. 54 concepts declare none today; recording that as *unconstrained*
   * would turn *we have not stated this* into *we have decided this*.
   */
  readonly constraint?: ValueConstraint;

  /**
   * **This concept is deliberately not offered, and why** (found rehearsing `#1319`, 2026-09-29).
   *
   * Every concept is offered, through {@link suggest} or the console's type-down list, or declares
   * this, and `resolvable-macros.test.ts` fails otherwise. `suggest` seeds a row from a pasted name
   * through the matcher, so a concept the matcher cannot reach is offered by the type-down instead
   * (`MACRO_HELPER_NAMES`): one with no path (produced, or read across a chain), or one never
   * inferred ({@link isInferable} — a `demandSource.` read, which has a path). Without it, *absent* meant both *we
   * decided against offering it* and *nobody added it*: twelve macros added by `1f-ii`, plus
   * `domain` and `device_type_code`, resolved and were offered nowhere, and the gate checked only
   * for more than one suggested name, never for none (`#1189`, `#1190`).
   */
  readonly notSuggested?: {
    /** Why — read by a person deciding whether the decline still holds. */
    readonly reason: string;
  };

  /**
   * **Spellings that must never be _inferred_ from somebody else's document** (#1190), with
   * the reason. This declines a *guess*, never the name: `{max_duration}` and `{bundle}` are
   * valid macros an operator may type, they resolve, and the picker's rules are untouched.
   * What is declined is the matcher proposing one from a name it did not author.
   *
   * **Absence has to mean one thing, and here it did not.** The declines lived in
   * `resolvable-macros.test.ts` (`max_duration`, `pod_ads`) and in a *code comment* on
   * `MACRO_DEFAULTS` (`bundle`) — so the matcher could not read either, and the reach gate
   * was keyed on *concept reachable by at least one spelling*, which a wider vocabulary
   * satisfies more thoroughly. Deriving the matcher from this file would have silently
   * reversed three deliberate decisions and **passed every gate**, which is #1189's own rule
   * — *declaring the exclusions is the work, the gate is the easy half* — arriving one layer
   * along, in the file written to end it.
   *
   * **Per spelling, not per concept**, because both shapes are real and one of them is a
   * whole entry: every spelling of `max_duration` is ambiguous against an ORTB
   * `imp.video.maxduration`, while `app_bundle` declines only its alias `bundle`.
   *
   * **The reason is required and is a claim that has to stay true** — removing a row is what
   * filling the hole looks like, and `field-catalog.spec.ts` fails on a declined spelling
   * this entry does not carry, so a typo cannot decline nothing.
   *
   * **Not scoped to a destination, and that is a decision rather than an omission.** #1190
   * observes that `maxduration` beside an ORTB video field means one ad's ceiling while a
   * VAST query parameter named `max_duration` probably does mean the pod's — so a decline
   * could legitimately be per-surface. It is declared flat because there is one matcher and
   * both paste paths are ambiguous in the same direction today; `D35`'s `when` is where a
   * scope would go if a surface ever disagrees, and widening this to carry one is an
   * addition rather than a change.
   */
  readonly neverInferred?: {
    /** The spellings declined. **Omit to decline every spelling of this entry.** */
    readonly spellings?: readonly string[];
    /** Why — read by a person deciding whether the decline still holds. */
    readonly reason: string;
  };
}

/**
 * Every name the resolver resolves, as concepts rather than as a flat list.
 *
 * **Derived from the resolver's `case` fall-through groups**, because that grouping already
 * *is* the concept/alias relation — authored, and correct by construction. It was then
 * hand-reviewed, which was necessary: a generator loses the twelve labels in
 * `resolveDeviceTypeCode`, which are `deviceType` **values** and not names at all, and
 * silently mistaking them for names is the defect that hid twelve false clearances inside
 * the gate built to find them.
 */
export const FIELD_CATALOG: readonly FieldCatalogEntry[] = [
  // ---- User / device identity ----
  {
    name: 'ip',
    suggest: 'ip',
    // `deviceip` and the other flat IAB spellings below are what a pasted tag's `[DEVICEIP]`
    // canonicalises to — resolved, and never suggested (D17). #1319.
    aliases: ['ip_address', 'deviceip'],
    path: 'session.user.ipAddress',
    chain: true,
    description: 'Viewer IP address',
    section: 'User / device identity',
    perRequest: true,
    constraint: IP_ADDRESS,
  },
  {
    name: 'ua',
    suggest: 'ua',
    // IAB has two: CLIENTUA (whoever made the request) and DEVICEUA. We are server-side and
    // forward the viewer's, so both are this one.
    aliases: ['user_agent', 'clientua', 'deviceua'],
    path: 'session.user.userAgent',
    chain: true,
    description: 'Device user agent',
    section: 'User / device identity',
    perRequest: true,
  },
  {
    name: 'ifa',
    suggest: 'ifa',
    aliases: ['idfa', 'did', 'device_id', 'deviceid'],
    path: 'session.user.idfa',
    chain: true,
    description: 'Advertising identifier (IFA/IDFA)',
    section: 'User / device identity',
    perRequest: true,
    constraint: ADVERTISING_ID,
  },
  {
    name: 'device_type',
    suggest: 'device_type',
    path: 'session.user.deviceType',
    description: 'Device type as reported (e.g. "ctv")',
    section: 'User / device identity',
  },
  // Distinct from `device_type` and easy to conflate: this is the **numeric ORTB code**,
  // derived from the string by `resolveDeviceTypeCode`. It has no path because it is
  // computed, which is why `{ctv}` is not a macro — `ctv` is an input *value* here.
  //
  // The lowercase `devicetype` spelling was **removed** (#1093): it is the OpenRTB field
  // name, and as a macro it sat one capital letter from `device_type`, returning `3` where
  // that returns `"ctv"`. The protocol field keeps its spelling on the wire.
  {
    name: 'device_type_code',
    produced: true,
    description: 'OpenRTB numeric device-type code, derived from the reported type',
    section: 'User / device identity',
  },
  {
    name: 'device_name',
    suggest: 'device_name',
    aliases: ['devicename'],
    path: 'session.user.deviceName',
    description: 'Device model name as reported',
    section: 'User / device identity',
  },
  {
    name: 'device_model',
    suggest: 'model',
    aliases: ['model'],
    path: 'session.user.device.model',
    description: 'Device model',
    section: 'User / device identity',
  },
  {
    name: 'device_make',
    suggest: 'make',
    aliases: ['make'],
    path: 'session.user.device.make',
    description: 'Device manufacturer',
    section: 'User / device identity',
  },
  {
    name: 'os',
    suggest: 'os',
    path: 'session.user.device.os',
    description: 'Operating system',
    section: 'User / device identity',
  },
  {
    name: 'osv',
    suggest: 'osv',
    aliases: ['os_version'],
    path: 'session.user.device.osVersion',
    description: 'Operating system version',
    section: 'User / device identity',
  },
  {
    name: 'hwv',
    suggest: 'hwv',
    aliases: ['hardware_version'],
    path: 'session.user.device.hwv',
    description: 'Hardware version',
    section: 'User / device identity',
  },
  {
    // IAB `[LATLONG]` (`#1319` item 2): `lat,lon`, from the location `/ortb` reads (`#1317`, `#1121`).
    // Nothing unless both coordinates are known.
    name: 'lat_long',
    aliases: ['latlong'],
    chain: true,
    description: 'Latitude and longitude, as "lat,lon"',
    section: 'User / device identity',
    perRequest: true,
  },
  {
    name: 'ifa_type',
    suggest: 'ifa_type',
    aliases: ['ifatype'],
    path: 'session.user.device.ifaType',
    description: 'Type of advertising identifier',
    section: 'User / device identity',
  },

  // ---- Client hints ----
  {
    name: 'sec_ch_ua',
    suggest: 'sec_ch_ua',
    aliases: ['sechua'],
    path: 'session.user.secChUa',
    description: 'User-Agent client hint',
    section: 'Client hints',
    perRequest: true,
  },
  {
    name: 'sec_ch_ua_mobile',
    suggest: 'sec_ch_ua_mobile',
    aliases: ['sechuamobile'],
    path: 'session.user.secChUaMobile',
    description: 'Mobile client hint',
    section: 'Client hints',
    perRequest: true,
  },
  {
    name: 'sec_ch_ua_platform',
    suggest: 'sec_ch_ua_platform',
    aliases: ['sechuaplatform'],
    path: 'session.user.secChUaPlatform',
    description: 'Platform client hint',
    section: 'Client hints',
    perRequest: true,
  },

  // ---- Privacy / regulatory ----
  {
    name: 'dnt',
    suggest: 'dnt',
    kind: 'flag',
    aliases: ['do_not_track'],
    path: 'session.user.dnt',
    description: 'Do Not Track flag (0/1)',
    section: 'Privacy / regulatory',
    perRequest: true,
    consent: true,
    constraint: FLAG_0_1,
  },
  {
    name: 'lmt',
    suggest: 'lmt',
    kind: 'flag',
    aliases: ['limit_ad_tracking', 'limitadtracking'],
    path: 'session.user.lmt',
    description: 'Limit Ad Tracking flag (0/1)',
    section: 'Privacy / regulatory',
    perRequest: true,
    consent: true,
    constraint: FLAG_0_1,
  },
  {
    name: 'us_privacy',
    suggest: 'us_privacy',
    aliases: ['usprivacy'],
    path: 'session.user.usPrivacy',
    description: 'US Privacy (CCPA) string',
    section: 'Privacy / regulatory',
    perRequest: true,
    consent: true,
    // **The case `#914` was raised from, and the only constraint declared here** (`D41`). A real
    // ORTB source had this set to `yes`; nothing told the operator, and an exchange that rejects it
    // does so with no explanation — if it rejects at all rather than ignoring the field.
    //
    // Declared on the **concept** rather than on either destination, which is what makes it apply
    // identically to the VAST query parameter and to `regs.ext.us_privacy` — the parity `#914`
    // requires, from one declaration. Populating the rest of its list is `#914`'s job; one field is
    // here because a mechanism proved only against fixtures is proved for the function and not for
    // the wiring (`#1186`).
    //
    // IAB CCPA v1: a version digit, then three of `Y`, `N` or `-`. `1---` is the neutral string the
    // resolver produces and must stay valid.
    constraint: {
      pattern: { source: '^[0-9][YyNn-]{3}$', describes: 'a CCPA string — a version digit then three of Y, N or -' },
    },
  },
  {
    name: 'gdpr',
    suggest: 'gdpr',
    kind: 'flag',
    /**
     * The **flat** field, not `user.consent.gdpr` (`#1157`, measured 2026-08-25; the block was retired by `#1526`).
     *
     * Three things point the same way. Production writes the flat home on every path — all three
     * controllers build `regs` and the hop carries `regs.gdpr ?? user.gdpr` — while `user.consent`
     * is written only by `/vmap`'s extension mapping and the test rig, so a `/vast` or `/ortb`
     * session could never resolve it. `consent` holds **booleans** where the wire wants `0`/`1`,
     * which is the type violation `b6c4fed9` fixed (`#1005`). And `us_privacy` and `gdpr_consent`
     * beside it already point at the flat fields, so this entry was the odd one out in its own
     * section.
     */
    path: 'session.user.gdpr',
    description: 'GDPR-applies flag (0/1)',
    section: 'Privacy / regulatory',
    perRequest: true,
    consent: true,
    constraint: FLAG_0_1,
  },
  {
    name: 'gdpr_consent',
    suggest: 'gdpr_consent',
    aliases: ['consent', 'gdprconsent'],
    path: 'session.user.gdprConsent',
    description: 'GDPR TC string',
    section: 'Privacy / regulatory',
    perRequest: true,
    consent: true,
    constraint: CONSENT_STRING,
  },
  {
    name: 'gpp',
    suggest: 'gpp',
    aliases: ['gppstring'],
    path: 'session.regs.gpp',
    description: 'GPP string, as the publisher sent it (#1319)',
    section: 'Privacy / regulatory',
    perRequest: true,
    consent: true,
  },
  {
    name: 'gpp_sid',
    suggest: 'gpp_sid',
    aliases: ['gppsectionid'],
    path: 'session.regs.gppSid',
    description: 'GPP section ids that apply, as the publisher sent them (#1319)',
    section: 'Privacy / regulatory',
    perRequest: true,
    consent: true,
  },
  {
    // IAB `[REGULATIONS]`: the regulations whose flag is 1 — `gdpr`, `coppa`. Built from the two flags.
    name: 'regulations',
    chain: true,
    description: 'Regulations that apply: gdpr and coppa, where their flag is 1',
    section: 'Privacy / regulatory',
    perRequest: true,
    consent: true,
  },
  {
    name: 'coppa',
    suggest: 'coppa',
    kind: 'flag',
    /** The flat field, for the reasons given on `gdpr` above (`#1157`). */
    path: 'session.user.coppa',
    description: 'COPPA flag (0/1)',
    section: 'Privacy / regulatory',
    perRequest: true,
    consent: true,
    constraint: FLAG_0_1,
  },

  // ---- Supply chain ----
  // No single path: read across the 2.6 spelling, the 2.5 spelling, a `?schain=` custom
  // field and the pod's flattened alias. Object-valued on the ORTB spellings, so it
  // resolves to nothing on a scalar surface until a rendering is authored (D23).
  {
    name: 'schain',
    aliases: ['supply_chain'],
    chain: true,
    description: 'Supply chain, as the caller supplied it — never one we build',
    section: 'Supply chain',
  },
  {
    // IAB `[TRANSACTIONID]` (`#1319` item 2): the initiating player's id for this chain of requests.
    name: 'transaction_id',
    suggest: 'transaction_id',
    aliases: ['transactionid'],
    path: 'session.sourceContext.tid',
    description: "The caller's transaction id (OpenRTB source.tid)",
    section: 'Supply chain',
    perRequest: true,
  },

  // ---- App / site context ----
  {
    name: 'app_bundle',
    suggest: 'app_bundle',
    aliases: ['bundle', 'appbundle'],
    path: 'session.context.appBundle',
    chain: true,
    description: 'App bundle ID or domain',
    section: 'App / site context',
    // Promoted from a comment on the console's `MACRO_DEFAULTS`, where it was a decision no
    // gate could read: a bare `bundle` would match every `app.bundle` sample leaf in a pasted
    // ORTB body, proposing a macro for a field the driver already builds procedurally. The
    // concept stays reachable under `app_bundle`, which is the spelling that cannot mean
    // anything else.
    neverInferred: { spellings: ['bundle'], reason: 'hijacks every app.bundle leaf in a pasted ORTB body' },
    constraint: BUNDLE_ID,
  },
  {
    name: 'app_name',
    suggest: 'app_name',
    aliases: ['appname'],
    path: 'session.context.appName',
    description: 'App name as the publisher reports it',
    section: 'App / site context',
  },
  {
    name: 'app_id',
    suggest: 'app_id',
    aliases: ['appid'],
    path: 'session.context.appId',
    // Not the bundle. `app_bundle` names the binary on its platform; this names the app
    // in the *exchange's* system, and neither is derivable from the other (#1091 · A).
    description: "The exchange's identifier for this app",
    section: 'App / site context',
  },
  {
    name: 'publisher_id',
    suggest: 'publisher_id',
    aliases: ['publisherid'],
    path: 'session.context.publisherId',
    description: "The publisher's identifier on the exchange",
    section: 'App / site context',
  },
  {
    name: 'publisher_name',
    suggest: 'publisher_name',
    aliases: ['publishername'],
    path: 'session.context.publisherName',
    description: 'Publisher name',
    section: 'App / site context',
  },
  {
    name: 'app_store_url',
    suggest: 'app_store_url',
    aliases: ['storeurl'],
    path: 'session.context.appStoreUrl',
    description: 'App store listing URL',
    section: 'App / site context',
    constraint: ABSOLUTE_URL,
  },
  {
    name: 'url',
    suggest: 'url',
    aliases: ['page', 'page_url', 'referrer', 'pageurl'],
    path: 'session.context.pageUrl',
    chain: true,
    description: 'Page or app URL the ad runs on',
    section: 'App / site context',
    constraint: ABSOLUTE_URL,
  },
  {
    name: 'domain',
    // `session.context.domain` has a writer since `#1317` (`/ortb`'s `site.domain` / `app.domain`);
    // still a chain, because the older pod and content spellings are read first.
    chain: true,
    description: 'Domain of the page or app',
    section: 'App / site context',
    constraint: HOSTNAME,
  },
  {
    name: 'connection_type',
    suggest: 'connection_type',
    aliases: ['connectiontype'],
    path: 'session.context.connectionType',
    description: 'Network connection type',
    section: 'App / site context',
  },
  {
    name: 'player_type',
    suggest: 'player_type',
    path: 'session.context.playerType',
    description: 'Player or SDK type',
    section: 'App / site context',
  },

  // ---- Player dimensions ----
  {
    name: 'w',
    suggest: 'w',
    aliases: ['width', 'player_width', 'playerwidth'],
    path: 'session.context.playerWidth',
    description: 'Player width (px)',
    section: 'Player dimensions',
    constraint: POSITIVE,
  },
  {
    name: 'h',
    suggest: 'h',
    aliases: ['height', 'player_height', 'playerheight'],
    path: 'session.context.playerHeight',
    description: 'Player height (px)',
    section: 'Player dimensions',
    constraint: POSITIVE,
  },
  {
    // IAB `[PLAYERSIZE]`: `w,h`, and nothing unless both are known.
    name: 'player_size',
    aliases: ['playersize'],
    chain: true,
    description: 'Player width and height, as "w,h"',
    section: 'Player dimensions',
  },

  // ---- Content ----
  {
    name: 'content_id',
    suggest: 'content_id',
    aliases: ['contentid'],
    path: 'session.content.contentId',
    description: 'Publisher content ID',
    section: 'Content',
  },
  {
    name: 'content_title',
    suggest: 'content_title',
    aliases: ['contenttitle'],
    path: 'session.content.contentTitle',
    description: 'Content title',
    section: 'Content',
  },
  // `rating` is canonical because it is the resolver's first label; the two `content*`
  // spellings are the ones an operator is more likely to write. Same value on all three.
  {
    name: 'rating',
    suggest: 'content_rating',
    aliases: ['contentrating', 'content_rating'],
    path: 'session.content.contentRating',
    chain: true,
    description: 'Content rating (e.g. TV-MA)',
    section: 'Content',
  },
  {
    name: 'content_length',
    suggest: 'content_length',
    aliases: ['contentlength'],
    path: 'session.content.contentLength',
    chain: true,
    description: 'Content length in seconds',
    section: 'Content',
    constraint: NON_NEGATIVE,
  },
  // Separate from `content_length` in the resolver, and reads the same session field.
  // Two entries for one value is #1077's shape; kept faithful to the resolver rather than
  // merged here, because merging them in the catalog without merging the `case` groups
  // would make this file disagree with the code it declares.
  {
    name: 'content_duration',
    aliases: ['contentduration', 'video_duration'],
    path: 'session.content.contentLength',
    chain: true,
    description: 'Content duration in seconds — a second spelling of content length',
    notSuggested: { reason: 'a second spelling of content length, which is offered as `content_length`' },
    section: 'Content',
    constraint: NON_NEGATIVE,
  },
  {
    name: 'genre',
    suggest: 'genres',
    aliases: ['genres'],
    path: 'session.content.genres',
    description: 'Content genre. A list is comma-joined, the IAB URL convention',
    section: 'Content',
  },
  {
    name: 'language',
    suggest: 'language',
    path: 'session.content.language',
    description: 'Content language (ISO-639-1)',
    section: 'Content',
    constraint: LANGUAGE_CODE,
  },

  {
    name: 'content_url',
    suggest: 'content_url',
    aliases: ['contenturl', 'contenturi'],
    path: 'session.content.contentUrl',
    description: 'URL of the content, for buyer contextual checks',
    section: 'Content',
    constraint: ABSOLUTE_URL,
  },
  {
    // IAB `[CONTENTCAT]` (`#1319` item 2). The session keeps the categories as the publisher sent
    // them; IAB asks for Content Taxonomy 2.x ids, and nothing here translates between taxonomies.
    name: 'content_categories',
    suggest: 'content_categories',
    aliases: ['contentcat'],
    path: 'session.content.iabCategories',
    description: 'Content categories, as the publisher sent them',
    section: 'Content',
  },
  {
    name: 'content_channel',
    suggest: 'content_channel',
    aliases: ['contentchannel'],
    path: 'session.content.contentChannel',
    // The feed, not the programmer — `content_network` is that, and a buyer prices them
    // differently, which is why they are two entries (#1091 · B).
    description: 'Channel the content runs on, e.g. "Discovery Channel"',
    section: 'Content',
  },
  {
    name: 'content_network',
    suggest: 'content_network',
    aliases: ['contentnetwork'],
    path: 'session.content.contentNetwork',
    description: 'Network the content belongs to, e.g. "Discovery"',
    section: 'Content',
  },

  // ---- Pod / session identity & sizing ----
  {
    name: 'ad_unit',
    suggest: 'ad_unit',
    // `tagid` is OpenRTB's name for this same value, so it is a spelling rather than a
    // second concept (D25's test: the values are identical). Deliberately **not**
    // `placement` — ORTB's `video.placement` is an enum of playback contexts and shares
    // nothing but the English word, which is the confusion D25 was written about.
    aliases: ['adunit', 'tagid'],
    path: 'session.adUnit',
    chain: true,
    description: "The publisher's ad unit / placement, sent as imp.tagid",
    section: 'Pod / session identity & sizing',
  },
  // **Suggested, and therefore matched from a pasted name** (`#1189`). These three were declared
  // here, offered in the type-down, and resolvable by the driver, while the console's matcher had
  // no entry for them at all — so a real body's `kvps.unique_pod_id` matched nothing by name and
  // was suggested `{ifa}` on the strength of its value being a UUID. `perRequest` is the other
  // half of why they must be matched: the literal in a sample is one viewer's session, and
  // declining to recognise the name is what leaves it frozen in the configuration.
  {
    name: 'session_id',
    suggest: 'session_id',
    aliases: ['sessionid'],
    path: 'session.sessionId',
    chain: true,
    description: 'bidBrain session identifier',
    section: 'Pod / session identity & sizing',
    perRequest: true,
  },
  {
    name: 'pod_id',
    suggest: 'pod_id',
    aliases: ['podid'],
    path: 'pod.podId',
    chain: true,
    description: 'Ad-break (pod) identifier',
    section: 'Pod / session identity & sizing',
    perRequest: true,
  },
  {
    name: 'pod_index',
    suggest: 'pod_index',
    aliases: ['podindex'],
    path: 'pod.podIndex',
    chain: true,
    description: 'Zero-based position of this break in playback',
    section: 'Pod / session identity & sizing',
  },
  {
    name: 'pod_max_dur',
    suggest: 'pod_max_duration',
    aliases: [
      'dur',
      'pod_max_duration',
      'maxpodduration',
      'max_pod_duration',
      'breakmaxduration',
      'seconds',
      'duration',
    ],
    path: 'pod.maxDuration',
    description: 'Maximum total pod duration (seconds)',
    section: 'Pod / session identity & sizing',
    // `seconds` and `duration` moved here from `max_duration` (`#1319` item 7), keeping what they
    // have always resolved to. They were never inferred and still are not: `duration` names a
    // content's, an ad's or a break's length equally.
    neverInferred: {
      spellings: ['seconds', 'duration'],
      reason: "names any length — a content's, an ad's or a break's (#1000, #1319)",
    },
    constraint: POSITIVE,
  },
  // ---- The called source's own bounds (`#1319` items 2 and 7) ----
  // Read from the source the request is being built for, in force (`effectiveTerms`), which a
  // `{macro}` can do since `BaseDriver` hands the source in. A surface with no called source — a
  // served pixel — resolves these to nothing.
  {
    name: 'max_duration',
    aliases: ['maxduration', 'breakmaxadlength'],
    path: 'demandSource.restrictions.maxDurationSeconds',
    description: 'Longest ad this source accepts (seconds) — one ad, not the break',
    section: 'Pod / session identity & sizing',
    // **Re-pointed to the per-ad maximum** (Todd, 2026-09-16). It was a second spelling of the pod
    // maximum and declined for inference as *ambiguous against `imp.video.maxduration`*. That reason
    // is gone, since they now mean the same thing, and it is still not inferred, for the reason every
    // `demandSource.` entry is not (`isInferable`).
    constraint: POSITIVE,
  },
  {
    name: 'min_duration',
    aliases: ['minduration', 'breakminadlength'],
    path: 'demandSource.restrictions.minDurationSeconds',
    description: 'Shortest ad this source accepts (seconds)',
    section: 'Pod / session identity & sizing',
    constraint: POSITIVE,
  },
  {
    name: 'blocked_ad_categories',
    aliases: ['blockedadcategories'],
    path: 'demandSource.restrictions.excludeIabCategories',
    description: 'IAB ad categories this source must not return',
    section: 'Pod / session identity & sizing',
  },
  {
    name: 'allowed_ad_categories',
    aliases: ['adcategories'],
    path: 'demandSource.restrictions.allowIabCategories',
    description: 'IAB ad categories this source may return — absent means all',
    section: 'Pod / session identity & sizing',
  },
  {
    name: 'pod_max_ads',
    suggest: 'pod_max_ads',
    aliases: ['breakmaxads'],
    path: 'pod.maxAds',
    description: 'Maximum number of ads in the pod',
    section: 'Pod / session identity & sizing',
    constraint: POSITIVE,
  },
  {
    name: 'pod_ads',
    aliases: ['podads', 'pod_ad_slots', 'max_ads', 'maxads'],
    path: 'pod.maxAds',
    chain: true,
    description: 'Ad slots in the pod — a second spelling of the pod maximum',
    section: 'Pod / session identity & sizing',
    // #1000's other half. `maxads` beside an ORTB `imp` array reads as that array's length.
    // Reachable under `pod_max_ads`.
    neverInferred: { reason: "ambiguous against an imp array's length (#1000)" },
    notSuggested: { reason: 'a second spelling of the pod maximum, which is offered as `pod_max_ads`' },
    constraint: POSITIVE,
  },

  // ---- Produced ----
  // `cb` is ours (D22 — *do we produce it?*), so it has no path and none should be offered.
  // `[timestamp]` is deliberately never suggested for it (D17): brackets are resolved so a
  // pasted GAM tag works, and never offered, because the bracket syntax is dead on ORTB.
  {
    name: 'cb',
    aliases: ['cachebuster', 'cachebusting'],
    produced: true,
    description: 'Cache buster — a fresh value per request',
    section: 'Produced',
    perRequest: true,
  },
  {
    name: 'timestamp',
    produced: true,
    description: 'Request timestamp in milliseconds',
    section: 'Produced',
    notSuggested: { reason: 'D17: a cache buster is offered as `{cb}`, one spelling for one job' },
  },
  // IAB `[SERVERSIDE]`: `1` on a request we send for a player, which is what a proxy is. Not on a
  // served pixel, which the player fires itself (IAB's `0`, and the value when the macro is absent).
  {
    name: 'server_side',
    aliases: ['serverside'],
    produced: true,
    description: "1 when bidBrain sends the request on the player's behalf",
    section: 'Produced',
  },
];

/**
 * The suggested name per concept, and **exactly one per concept** — the picker's list.
 *
 * A concept is offered only when it declares {@link FieldCatalogEntry.suggest}; the rest
 * resolve and match without being suggested. Two names for one path is the state this
 * replaced, where an operator chose between `{ip}` and `{ipAddress}` with no information.
 */
export function suggestedNames(): string[] {
  return FIELD_CATALOG.map((e) => e.suggest).filter((n): n is string => n !== undefined);
}

/** Every spelling of an entry — its canonical name and its aliases, in that order. */
export function spellingsOf(entry: FieldCatalogEntry): readonly string[] {
  return [entry.name, ...(entry.aliases ?? [])];
}

/** Whether `spelling` of `entry` may be inferred from a name we did not author. */
export function isInferable(entry: FieldCatalogEntry, spelling: string): boolean {
  // **A value the called source's authored configuration holds is never inferred** (`#1319`).
  // Inference turns a pasted document's literal into a mapping, which is right for a session value
  // the request will carry and wrong for a source's own bound: a pasted `maxduration: 60` became a
  // read of this source's restriction, unset on most sources, so the leaf resolved to nothing and
  // the operator's number was dropped. Found by `mapping-suggest.test.ts` when the per-entry decline
  // was first lifted. A rule on the root rather than a decline per entry, so the next bound added
  // under `demandSource.` inherits it; the macro is still one anyone may type.
  if (entry.path?.startsWith('demandSource.')) return false;
  const declined = entry.neverInferred;
  if (!declined) return true;
  // No `spellings` list declines the whole entry — see {@link FieldCatalogEntry.neverInferred}.
  return declined.spellings !== undefined && !declined.spellings.includes(spelling);
}

/**
 * **The matcher's vocabulary: every spelling that may be inferred, mapped to its concept.**
 *
 * This replaces `catalogPathsByName`, which was written for exactly this job, described in
 * its own docstring as *"the matcher's wide vocabulary"*, and had **zero callers repo-wide**
 * — so `MACRO_DEFAULTS` stayed hand-kept beside it and drifted, which is #1189. Two things
 * made the old shape unusable rather than merely unused, and both are why this returns an
 * **entry** rather than a path:
 *
 * - **A name→path map cannot carry a decline.** Three spellings are deliberately not
 *   inferable and the map had no room to say so, so wiring it would have reversed them
 *   silently (see {@link FieldCatalogEntry.neverInferred}).
 * - **A name→path map cannot reach a produced or chained concept at all.** `cb`,
 *   `timestamp`, `device_type_code`, `schain` and `domain` have no single path to be keyed
 *   on, so five concepts were unreachable *by construction* rather than by decision — a
 *   pasted `cb=1234` matched nothing. Keying on the concept is what closes that, and the
 *   path becomes what the match *carries* rather than what it is looked up by.
 */
export function inferableSpellings(): ReadonlyMap<string, FieldCatalogEntry> {
  const out = new Map<string, FieldCatalogEntry>();
  for (const entry of FIELD_CATALOG) {
    for (const spelling of spellingsOf(entry)) {
      if (isInferable(entry, spelling)) out.set(spelling, entry);
    }
  }
  return out;
}

/** Every spelling in the catalog — canonical names and aliases alike. */
export function allCatalogNames(): string[] {
  return FIELD_CATALOG.flatMap((e) => [e.name, ...(e.aliases ?? [])]);
}

/** The entry a spelling belongs to, or `undefined` if the name is not addressable. */
export function catalogEntryFor(name: string): FieldCatalogEntry | undefined {
  const canonical = name
    .trim()
    .replace(/^[[{]+|[\]}]+$/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase();

  return FIELD_CATALOG.find((e) => e.name === canonical || (e.aliases?.includes(canonical) ?? false));
}

/* ------------------------------------------------------------------------- *
 * Version-scoped wire expression (#1112 · D29)
 * ------------------------------------------------------------------------- */

/** The OpenRTB versions a demand source may declare. */
export type ProtocolVersion = '2.5' | '2.6';

/** Normalise a source's free-form `version` to one we have a declaration for. */
export function normalizeProtocolVersion(version?: string): ProtocolVersion {
  return String(version ?? '')
    .trim()
    .startsWith('2.5')
    ? '2.5'
    : '2.6';
}

/** How one concept is expressed on one protocol version. */
export interface WireExpression {
  /** The field name this version uses, relative to the object the concept lives on. */
  readonly field: string;

  /**
   * Concept member → the value this version's enumeration gives it.
   *
   * A member absent from this map is **not expressible on this version**, and that is a
   * declaration rather than an omission: see {@link ProtocolConcept}.
   */
  readonly values: Readonly<Record<string, number>>;
}

/**
 * One concept whose **name and value both depend on the protocol version** (D29).
 *
 * `video.placement` (2.5) and `video.plcmt` (2.6) are the worked example and the reason
 * this is not modelled as an alias pair. An alias pair keeps a *protocol enumeration* as
 * the canonical vocabulary, so every version added needs a translation to every other one;
 * a **concept** vocabulary needs one row per version, and the row is checkable against
 * that version's spec on its own.
 *
 * It also makes the lossy direction *declarable*. 2.6's `plcmt` says what kind of content
 * the ad accompanies; 2.5's `placement` says where the *unit* sits. They are not two
 * spellings of one list, so some members have no faithful 2.5 value — and **omitting a
 * member from {@link WireExpression.values} is the explicit statement D25 asks for**, not a
 * gap. Approximating it would send a request that is syntactically fine and semantically
 * wrong, which nothing downstream can detect.
 */
export interface ProtocolConcept {
  /** bidBrain's name for the concept — the vocabulary an operator would author against. */
  readonly concept: string;

  /**
   * The object this concept lives on, relative to the request root — `video` today.
   *
   * Declared rather than inferred because {@link WireExpression.field} is *relative*, so
   * `plcmt` alone does not say where it goes. It is also what lets one pass find every
   * concept on a built request instead of a driver naming them one at a time, which is the
   * shape this file exists to stop.
   */
  readonly on: string;

  /** Every member of the concept vocabulary. Modelled on the newest version's list. */
  readonly members: readonly string[];

  /**
   * What bidBrain asserts when nothing has said otherwise.
   *
   * **Required, because both concepts already asserted one — as a literal.** `placement: 1`
   * and `linearity: 1` were constants in `openrtb.driver.ts`, so the assumption was being
   * made on every request and was readable nowhere. Declaring it does not add a claim; it
   * moves an existing one somewhere it can be seen, argued with, and overridden.
   *
   * It stays required until a concept genuinely declines to assume — a **restriction** on
   * what may be served (`skip`) rather than a description of the inventory, where silence is
   * the honest answer and inventing one would be a fabricated signal an exchange cannot tell
   * from a measured one. That is the moment to make it optional and say why, not before
   * (*a capability is created when a type declines something deliberately*, `A8`).
   */
  readonly assumed: string;

  readonly description: string;

  /** The expression per version. Every version we claim to speak appears here. */
  readonly byVersion: Readonly<Record<ProtocolVersion, WireExpression>>;
}

/**
 * The video concepts an operator authors by **meaning** rather than by magic number.
 *
 * **This list widened on 2026-08-27, and the reason is worth keeping** (`D9` — *a pass
 * widens past the question that opened it*). It was introduced for `placement` alone and
 * described as *"the concepts whose wire expression is version-scoped"*, with a standing
 * note that `linearity` *"needs no row here"* because it is unchanged between 2.5 and 2.6.
 * That is true and it was the wrong test: **version drift is one thing a concept can
 * express, not what a concept is.** What `ProtocolConcept` actually models is *the operator
 * authors a member of a vocabulary and we render it for the receiver* — and `linearity`,
 * offered as a bare `number` with `1=linear, 2=non-linear` in its description, is the same
 * defect one step earlier: the operator is being asked to know the IAB's list.
 *
 * So a concept is declared when **the member vocabulary is the operator's language and the
 * integer is the wire's**. A version-identical concept simply repeats its row, which is
 * cheap and reads correctly — `linearity` says *the same field, the same values, on both*,
 * which is a declaration rather than an omission.
 *
 * **The values are the IAB's, not ours** (*never invent a third party's capability*).
 * 2.5 List 5.9 *Video Placement Type* and 2.6's *Plcmt Subtype — Video* are separate lists
 * with separate meanings; only `instream` corresponds cleanly, which is exactly why 2.6
 * introduced a new field instead of extending the old one. `linearity` is List 5.7 and is
 * unchanged.
 *
 * `skip` is deliberately still absent: it is a **restriction on what may be served** rather
 * than a description of the inventory, so it sorts with #1111 rather than here.
 */
export const ORTB_VIDEO_CONCEPTS: readonly ProtocolConcept[] = [
  {
    concept: 'placement',
    on: 'video',
    members: ['instream', 'accompanying', 'interstitial', 'standalone'],
    assumed: 'instream',
    description: 'Where the ad sits relative to the content it plays with',
    byVersion: {
      // 2.6 · Plcmt Subtype — Video. The concept vocabulary is this list, because it is
      // the current one and the more precise one.
      '2.6': {
        field: 'plcmt',
        values: { instream: 1, accompanying: 2, interstitial: 3, standalone: 4 },
      },
      // 2.5 · List 5.9 Video Placement Type. `1` is In-Stream and corresponds. The rest of
      // 2.5's list (In-Banner, In-Article, In-Feed, Interstitial/Slider/Floating) describes
      // where a *unit* sits on a page, which is not what the 2.6 members say — so only
      // `instream` is declared, and the others are stated as unexpressible by being absent.
      '2.5': {
        field: 'placement',
        values: { instream: 1 },
      },
    },
  },
  {
    concept: 'linearity',
    on: 'video',
    members: ['linear', 'nonlinear'],
    // The driver already asserted this, as `linearity: 1` beside `placement: 1`. It is the
    // same assumption `placement: instream` makes about the same inventory, so declaring one
    // and not the other would leave the pair able to disagree.
    assumed: 'linear',
    description: 'Whether the ad plays in the content stream or over it',
    byVersion: {
      // IAB List 5.7 · Video Linearity. Identical on both versions, and stated twice
      // rather than defaulted: "the same on 2.5" is a claim, and a row is how this file
      // makes a claim checkable against that version's spec on its own.
      '2.6': { field: 'linearity', values: { linear: 1, nonlinear: 2 } },
      '2.5': { field: 'linearity', values: { linear: 1, nonlinear: 2 } },
    },
  },
];

/** Every concept that lives on one object of the request — `video` today. */
export function conceptsOn(object: string): readonly ProtocolConcept[] {
  return ORTB_VIDEO_CONCEPTS.filter((c) => c.on === object);
}

/**
 * Every field name **any** version uses for a concept.
 *
 * The rewrite pass needs this to clear the other version's spelling before writing its
 * own: a body that carried both `plcmt` and `placement` would be two answers on the wire,
 * and an exchange reading the one we did not mean has no way to know.
 */
export function wireFieldNamesFor(concept: string): readonly string[] {
  const declared = protocolConceptFor(concept);
  if (!declared) return [];
  return [...new Set(Object.values(declared.byVersion).map((e) => e.field))];
}

/** The concept declaration for a name, or `undefined` if it is not version-scoped. */
export function protocolConceptFor(concept: string): ProtocolConcept | undefined {
  return ORTB_VIDEO_CONCEPTS.find((c) => c.concept === concept);
}

/**
 * The field name and value one concept member takes on one version.
 *
 * Returns `undefined` when the version cannot express the member — the caller **omits the
 * field** rather than substituting a neighbour. A field absent is a fact an exchange can
 * act on; a field present with the wrong meaning is not.
 */
export function wireExpressionFor(
  concept: string,
  member: string,
  version?: string,
): { field: string; value: number } | undefined {
  const declared = protocolConceptFor(concept);
  if (!declared) return undefined;

  const expression = declared.byVersion[normalizeProtocolVersion(version)];
  const value = expression.values[member];

  return value === undefined ? undefined : { field: expression.field, value };
}

/**
 * **The member a wire value means, read by the field name it arrived under** — the inverse of
 * {@link wireExpressionFor}, for reading somebody else's request.
 *
 * The field name is the evidence, not the source's declared version: `placement: 1` is 2.5's list
 * and `plcmt: 1` is 2.6's, and a partner's sample is in whichever enumeration its own field name
 * belongs to. `undefined` where no version spelling the concept that way declares the value — which
 * is an honest *we cannot say*, never a nearest neighbour.
 */
export function conceptMemberForWireValue(concept: string, field: string, value: unknown): string | undefined {
  const declared = protocolConceptFor(concept);
  if (!declared) return undefined;
  for (const expression of Object.values(declared.byVersion)) {
    if (expression.field !== field) continue;
    const member = Object.entries(expression.values).find(([, v]) => v === value)?.[0];
    if (member) return member;
  }
  return undefined;
}

/** The versions that spell a concept with this field name — `['2.5']` for `placement`, both for `linearity`. */
export function wireFieldVersions(concept: string, field: string): ProtocolVersion[] {
  const declared = protocolConceptFor(concept);
  if (!declared) return [];
  return (Object.entries(declared.byVersion) as [ProtocolVersion, WireExpression][])
    .filter(([, expression]) => expression.field === field)
    .map(([version]) => version)
    .sort();
}

/**
 * A wire value no version's row maps to a member, kept **with the field name it arrived under**
 * (`#1482`, Todd 2026-09-29). 2.5's `placement: 3` is In-Article, and 2.6 has no member meaning
 * that, so the value is neither a member nor something to guess one from. The field name is what
 * says which enumeration the number belongs to, so it is what lets a receiver that spells the
 * concept the same way be sent the value unchanged, and every other receiver be sent nothing.
 */
export interface UnmappedConceptValue {
  readonly field: string;
  readonly value: number;
}

/** What a caller stated about a concept: a member, or a value no member means. */
export type CallerStatedConcept =
  | { readonly member: string; readonly unmapped?: undefined }
  | { readonly member?: undefined; readonly unmapped: UnmappedConceptValue };

/**
 * **Read a concept off somebody else's object** (`#1482`) — `imp.video` on an inbound bid request.
 *
 * Every spelling any version uses is read, newest version first, and the field name decides which
 * enumeration the number is in (see {@link conceptMemberForWireValue}). A number no row maps is
 * returned as `unmapped` with its field, never rounded to a neighbour. A value that is not an
 * integer is not a statement of this concept in any version, so it reads as nothing said.
 */
export function readConceptFromWire(concept: string, host: unknown): CallerStatedConcept | undefined {
  const declared = protocolConceptFor(concept);
  if (!declared || !host || typeof host !== 'object') return undefined;

  const newestFirst = (Object.keys(declared.byVersion) as ProtocolVersion[]).sort().reverse();
  const fields = [...new Set(newestFirst.map((version) => declared.byVersion[version].field))];

  for (const field of fields) {
    const raw = (host as Record<string, unknown>)[field];
    const value = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
    if (typeof value !== 'number' || !Number.isInteger(value)) continue;
    const member = conceptMemberForWireValue(concept, field, value);
    return member ? { member } : { unmapped: { field, value } };
  }
  return undefined;
}

/**
 * **A member stated by name** — `placement=instream` on `/vast` (`#1482`), where no field name says
 * which version's enumeration a number would be in, so only a member is a statement. Case and
 * surrounding space are not part of a name. `undefined` for anything that is not a member.
 */
export function readConceptMember(concept: string, raw: unknown): string | undefined {
  const declared = protocolConceptFor(concept);
  if (!declared || typeof raw !== 'string') return undefined;
  const name = raw.trim().toLowerCase();
  return declared.members.includes(name) ? name : undefined;
}

/** How one concept member came out for one version. */
export type ConceptRendering =
  /** Rendered — write `field` with `value` on the concept's host object. */
  | { readonly outcome: 'rendered'; readonly field: string; readonly value: number }
  /**
   * This version has **no faithful value** for the member, so the field is omitted and the
   * omission is reported. Never approximated: 2.5 has nothing that means 2.6's
   * `accompanying`, and sending its nearest neighbour asserts something about the
   * publisher's inventory that is not true.
   */
  | { readonly outcome: 'unexpressible'; readonly reason: string }
  /** The member is not in the concept's vocabulary — an authoring error, reported as one. */
  | { readonly outcome: 'unknown-member'; readonly reason: string }
  /** Nothing declares this concept, so there is nothing to render. */
  | { readonly outcome: 'not-a-concept' };

/**
 * **Render one concept member for one version — the single evaluator** (`N11h`, #1112).
 *
 * It lives here rather than in `wire-receiver.ts`, where it was written, because the
 * **console needs the same answer**: the ORTB body row states which field this leaf becomes
 * and what happens when this version cannot express the member, and a console copy of that
 * arithmetic would show the operator something the driver does not send. *One matcher per
 * vocabulary*, and this file is the half the console can reach — `wire-receiver` carries a
 * `DemandSourceMethod` and is not in the shared subgraph.
 *
 * `renderConceptForReceiver` is the receiver-shaped wrapper over this; it adds the one thing
 * a receiver knows that a version string does not, which is *nobody said*.
 */
export function renderConceptMember(concept: string, member: string, version?: string): ConceptRendering {
  const declared = protocolConceptFor(concept);
  if (!declared) return { outcome: 'not-a-concept' };

  if (!declared.members.includes(member)) {
    return {
      outcome: 'unknown-member',
      reason: `'${member}' is not a ${concept} — expected one of ${declared.members.join(', ')}.`,
    };
  }

  const expression = wireExpressionFor(concept, member, version);
  if (!expression) {
    return {
      outcome: 'unexpressible',
      reason: `OpenRTB ${normalizeProtocolVersion(version)} has no ${concept} value meaning '${member}', so the field is omitted rather than approximated.`,
    };
  }

  return { outcome: 'rendered', field: expression.field, value: expression.value };
}

/**
 * The concept a wire field name belongs to — `plcmt` and `placement` are both the
 * `placement` concept, on the version that spells it that way.
 *
 * **The synonym half of #1112.** An operator on 2.6 looking for `plcmt` is looking for this
 * concept; the catalog offers it under one name and the row now renders it under the
 * receiver's. Matching by *how any version spells it* is what keeps the two from being a
 * vocabulary the operator has to learn, and it is derived from the declaration rather than
 * hand-kept — which is the difference between a synonym list and the next stale list.
 */
export function protocolConceptForWireField(field: string): ProtocolConcept | undefined {
  return ORTB_VIDEO_CONCEPTS.find(
    (c) => c.concept === field || Object.values(c.byVersion).some((e) => e.field === field),
  );
}
